/* Collection page customization for the PSYKHE app block.
   Uses public PSYKHE custom-element render callbacks to match the Noli theme. */

(() => {
  const VERSION = "collection-render";
  const BLOCK_SELECTOR = '[id*="__psykhe_ai_the_noli_shop_collection_v2_"]';
  const FILTERS_EXPANDED_CLASS = "psykhe-noli-filters-expanded";
  const CONTROL_CHEVRON = "\u2304";
  const SIBLING_SWATCH_DATA_ID = "psykhe-noli-sibling-swatches-data";
  const SHOPIFY_PRICE_LOOKUP_STORAGE_KEY = "shopifyPriceLookup";
  const PRICE_LOOKUP_RETRY_LIMIT = 20;
  const PRICE_LOOKUP_RETRY_DELAY_MS = 500;
  const CARD_EXPORTPARTS = [
    "card:noli-card-shell",
    "link:noli-card-link",
    "media:noli-card-media",
    "image:noli-card-image-primary",
    "image-secondary:noli-card-image-secondary",
    "content:noli-card-meta",
    "title:noli-card-title",
    "pricing:noli-card-price",
    "price:noli-card-price-current",
    "price-sale:noli-card-price-sale",
    "compare-price:noli-card-compare",
    "actions:noli-card-actions",
  ].join(",");
  const MAX_INSTALL_ATTEMPTS = 80;
  let installAttempts = 0;
  let siblingSwatchDataCache = null;
  let presentmentPriceResolver = null;
  let presentmentPriceResolverFactory = null;
  let priceLookupRefreshTimer = null;
  let priceLookupRefreshAttempts = 0;

  function scopedBlocks() {
    const blocks = Array.from(document.querySelectorAll(BLOCK_SELECTOR));
    return blocks.filter((block) => !blocks.some((candidate) => candidate !== block && candidate.contains(block)));
  }

  function scopedElements(selector) {
    const roots = scopedBlocks();
    if (!roots.length) return [];
    return roots.flatMap((root) => Array.from(root.querySelectorAll(selector)));
  }

  function firstScopedElement(selector) {
    return scopedElements(selector)[0] || null;
  }

  function safeId(value) {
    return String(value || "block").replace(/[^A-Za-z0-9_-]/g, "-");
  }

  function createFilterIcon() {
    const wrapper = document.createElement("span");
    wrapper.className = "icon icon-new icon-filter psykhe-noli-filter-icon";
    wrapper.setAttribute("aria-hidden", "true");
    wrapper.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" focusable="false" xmlns="http://www.w3.org/2000/svg"><path d="M2.302 16.751h3.394m16 0H11.583" stroke="currentColor" stroke-width="1.358" stroke-miterlimit="6.667" stroke-linecap="square" stroke-linejoin="round"/><path d="M8.606 19.66a2.91 2.91 0 1 0 0-5.818 2.91 2.91 0 0 0 0 5.818z" stroke="currentColor" stroke-width="1.358" stroke-miterlimit="10" stroke-linecap="round"/><path d="M2.302 8.024h10.182m9.212 0h-3.394" stroke="currentColor" stroke-width="1.358" stroke-miterlimit="6.667" stroke-linecap="square" stroke-linejoin="round"/><path d="M15.393 10.933a2.91 2.91 0 1 0 0-5.818 2.91 2.91 0 0 0 0 5.818z" stroke="currentColor" stroke-width="1.358" stroke-miterlimit="10" stroke-linecap="round"/></svg>';
    return wrapper;
  }

  function createChevron() {
    const chevron = document.createElement("span");
    chevron.className = "icon icon-new icon-chevron-small psykhe-noli-control-chevron";
    chevron.setAttribute("aria-hidden", "true");
    chevron.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" focusable="false" xmlns="http://www.w3.org/2000/svg"><path d="M1.875 7.438 12 17.563 22.125 7.438" stroke="currentColor" stroke-width="2.75"/></svg>';
    return chevron;
  }

  function createControlButton(className, label, id) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = className;
    if (id) button.id = id;
    button.append(createFilterIcon());
    const text = document.createElement("span");
    text.className = "filter-bar__button-text";
    text.textContent = label;
    button.append(text, createChevron());
    return button;
  }

  function isMobileControlsViewport() {
    return window.matchMedia("(max-width: 749px)").matches;
  }

  function configureFilterPanel(panel, mode) {
    panel.setAttribute("mode", mode);
    panel.setAttribute("facet-summary", "auto");
    panel.setAttribute("hierarchical-mode", "tree");
    panel.setAttribute("pill-icon", CONTROL_CHEVRON);
    if (mode === "accordion") {
      panel.setAttribute("single-open", "");
      panel.setAttribute("max-visible-items", "0");
      panel.removeAttribute("close-facet-on-click-away");
    } else {
      panel.removeAttribute("single-open");
      panel.removeAttribute("max-visible-items");
      panel.setAttribute("close-facet-on-click-away", "");
    }
    panel.removeAttribute("scroll-on-expand");

    if (!customElements.get("psykhe-filter-panel")) return;
    panel.mode = mode;
    panel.pillIcon = CONTROL_CHEVRON;
    panel.renderOption = undefined;
    panel.renderFacet = mode === "bar" ? renderThemeBarFacet : renderThemeDrawerFacet;
    if (mode === "accordion") {
      panel.singleOpen = true;
      panel.closeFacetOnClickAway = false;
      panel.scrollOnExpand = null;
      panel.maxVisibleItems = 0;
    } else {
      panel.singleOpen = false;
      panel.closeFacetOnClickAway = true;
      panel.scrollOnExpand = null;
    }
  }

  function isColorFacet(value) {
    const key = `${value?.facetKey || ""} ${value?.facetLabel || ""}`.toLowerCase();
    return key.includes("color") || key.includes("colour") || key.includes("sibling_option");
  }

  function renderThemeFilterOption(option, ctx) {
    const selected = Boolean(option.selected);
    const disabled = Boolean(option.disabled);
    const swatchType = isColorFacet(option);

    return ctx.html`
      <li
        class=${swatchType ? "filter-item filter-item--swatch" : "filter-item"}
        part=${selected
          ? "option option-selected noli-filter-item noli-filter-item-selected"
          : disabled
          ? "option option-disabled noli-filter-item noli-filter-item-disabled"
          : "option noli-filter-item"}
      >
        <label
          class=${disabled ? "filter-item__content filter-item__content--disabled" : "filter-item__content"}
          part="noli-filter-option"
        >
          <input
            class="filter-item__checkbox visually-hidden"
            part="option-input noli-filter-option-input"
            type="checkbox"
            name=${option.facetKey}
            value=${String(option.value)}
            .checked=${selected}
            ?disabled=${disabled}
            @change=${ctx.actions.toggle(option)}
          />
          <span class="filter-item__content-inner" part="noli-filter-option-inner option-label">
            ${swatchType
              ? ctx.html`<span
                  class="filter-item__swatch"
                  data-shape="round"
                  part="noli-filter-option-swatch"
                  aria-hidden="true"
                  style=${swatchStyle({ value: swatchValueFromTheme(option.label) })}
                ></span>`
              : ctx.html`<span class="filter-input__input" part="noli-filter-checkbox-indicator" aria-hidden="true">
                  <span part=${selected ? "noli-filter-checkbox-check noli-filter-checkbox-check-selected" : "noli-filter-checkbox-check"}>✓</span>
                </span>`}
            <span class="filter-item__label" part="noli-filter-option-text">${option.label}</span>
            <span class="filter-item__count fs-body-75" part="option-count noli-filter-option-count">${option.count}</span>
          </span>
        </label>
      </li>
    `;
  }

  function renderSelectedFacetSummary(facet) {
    const selected = facet.options.filter((option) => option.selected);
    if (!selected.length) return "0 selected";
    if (selected.length === 1) return selected[0].label;
    return `${selected.length} selected`;
  }

  function facetHasSelection(facet) {
    return facet.selectedCount > 0 || facet.options.some((option) => option.selected);
  }

  function providerAttribute(name) {
    return firstScopedElement("psykhe-provider")?.getAttribute(name)?.trim() || "";
  }

  function storefrontLocale() {
    return document.documentElement.lang || navigator.language || undefined;
  }

  function shopCurrencyCode() {
    return providerAttribute("currency") || "USD";
  }

  function renderThemeBarFacet(facet, ctx) {
    const hasSelection = facetHasSelection(facet);
    const summary = renderSelectedFacetSummary(facet);

    return ctx.html`
      <div class="filter-group__values-inner" part="noli-facet-menu">
        <div class="filter-group__values-header" part="noli-facet-header">
          <span class="filter-group__values-active-values" part="noli-facet-active-values">${summary}</span>
          <button
            class=${hasSelection ? "filter-group__reset active" : "filter-group__reset"}
            part=${hasSelection ? "noli-facet-clear noli-facet-clear-active" : "noli-facet-clear"}
            type="button"
            ?disabled=${!hasSelection}
            @click=${ctx.actions.clearFacet(facet.key)}
          >
            Clear
          </button>
        </div>
        <ul class="filter-group__list" part="noli-filter-list">
          ${facet.options.map((option) => renderThemeFilterOption(option, ctx))}
        </ul>
      </div>
    `;
  }

  function renderThemeDrawerFacet(facet, ctx) {
    const hasSelection = facetHasSelection(facet);
    const summary = renderSelectedFacetSummary(facet);

    return ctx.html`
      <div class="filter-drawer__group animation--filter-drawer-item" part="noli-drawer-facet">
        <button
          type="button"
          class="filter-drawer__group-toggle"
          part="noli-drawer-facet-trigger"
          aria-expanded=${String(facet.expanded)}
          @click=${ctx.actions.toggleFacetOpen(facet)}
        >
          <span class="filter-drawer__group-toggle-inner" part="noli-drawer-facet-trigger-inner">
            <span class="filter-drawer__group-toggle-label" part="noli-drawer-facet-label">
              ${facet.label}
              ${hasSelection
                ? ctx.html`<span class="filter-drawer__group-toggle-count fs-body-50" part="noli-drawer-facet-count">${facet.selectedCount || 1}</span>`
                : ctx.nothing}
            </span>
            ${hasSelection
              ? ctx.html`<span class="filter-drawer__group-toggle-active-values fs-body-50" part="noli-drawer-facet-active-values">${summary}</span>`
              : ctx.nothing}
          </span>
          <span class="icon icon-new icon-chevron-small" part="noli-drawer-facet-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" focusable="false" fill="none"><path d="M1.875 7.438 12 17.563 22.125 7.438" stroke="currentColor" stroke-width="2.75"></path></svg>
          </span>
        </button>
        <div
          class="filter-drawer__group-filter-wrapper"
          part="noli-drawer-facet-panel"
          aria-hidden=${String(!facet.expanded)}
          ?hidden=${!facet.expanded}
        >
          <ul class="filter-drawer__list${facet.options.some(isColorFacet) ? " has-swatch" : ""}" part="noli-drawer-filter-list">
            ${facet.options.map((option) => renderThemeFilterOption(option, ctx))}
          </ul>
        </div>
      </div>
    `;
  }

  function renderThemeSortOption(option, ctx) {
    return ctx.html`
      <div
        class="filter-item"
        part=${option.selected ? "option option-selected noli-sort-option" : "option noli-sort-option"}
        role="option"
        aria-selected=${String(option.selected)}
        tabindex="-1"
        @click=${() => ctx.actions.select(option.value)}
      >
        <label class="filter-item__content" part="noli-sort-option-content">
          <input
            type="radio"
            class="filter-item__radio visually-hidden"
            part="noli-sort-option-input"
            name="sort_by"
            value=${option.value}
            .checked=${option.selected}
            @change=${() => ctx.actions.select(option.value)}
          />
          <span class="filter-item__content-inner" part="noli-sort-option-inner">
            <span class="filter-input__input" part="option-indicator noli-sort-option-indicator">
              <span
                class="indicator"
                part=${option.selected
                  ? "noli-sort-option-indicator-dot noli-sort-option-indicator-dot-selected"
                  : "noli-sort-option-indicator-dot"}
              ></span>
            </span>
            <span class="filter-item__label" part="option-label noli-sort-option-label">${option.label}</span>
          </span>
        </label>
      </div>
    `;
  }

  function ensureDrawerFilterPanel(drawer) {
    let body = drawer.querySelector(".psykhe-noli-filter-drawer__body");
    if (!body) {
      body = document.createElement("div");
      body.className = "psykhe-noli-filter-drawer__body";

      const filterPanel = document.createElement("psykhe-filter-panel");
      filterPanel.className = "psykhe-noli-drawer-filter-panel";
      configureFilterPanel(filterPanel, "accordion");

      body.append(filterPanel);
      drawer.append(body);
    }
    body.querySelectorAll(":scope > psykhe-active-filters").forEach((node) => node.remove());

    const drawerFilter = body.querySelector("psykhe-filter-panel");
    if (drawerFilter) configureFilterPanel(drawerFilter, "accordion");

    let footer = drawer.querySelector(".psykhe-noli-filter-drawer__footer");
    if (!footer) {
      footer = document.createElement("div");
      footer.className = "psykhe-noli-filter-drawer__footer";
      footer.setAttribute("slot", "footer");
      drawer.append(footer);
    }

    let footerActiveFilters = footer.querySelector(".psykhe-noli-filter-drawer__footer-active-filters");
    if (!footerActiveFilters) {
      footerActiveFilters = document.createElement("div");
      footerActiveFilters.className = "filter-drawer__footer-active-filters psykhe-noli-filter-drawer__footer-active-filters";
      footer.prepend(footerActiveFilters);
    }

    footerActiveFilters.querySelectorAll(":scope > psykhe-active-filters").forEach((node) => node.remove());

    let footerButtons = footer.querySelector(".psykhe-noli-filter-drawer__footer-buttons");
    if (!footerButtons) {
      footerButtons = document.createElement("div");
      footerButtons.className = "filter-drawer__footer-buttons psykhe-noli-filter-drawer__footer-buttons";
      footer.append(footerButtons);
    }

    footer.querySelectorAll("psykhe-clear-filters").forEach((node) => node.remove());

    let apply = footer.querySelector(".psykhe-noli-filter-drawer__apply");
    if (!apply) {
      apply = document.createElement("button");
      apply.type = "button";
      apply.textContent = "DONE";
      apply.setAttribute("data-psykhe-drawer-close", "");
    }
    apply.classList.add("filter-drawer__button-apply", "btn", "btn--primary", "psykhe-noli-filter-drawer__apply");

    if (apply.parentElement !== footerButtons) footerButtons.append(apply);
  }

  function syncFilterDrawerTrigger(drawer, triggerId) {
    const media = window.matchMedia("(max-width: 749px)");
    const apply = () => {
      if (media.matches) {
        drawer.setAttribute("trigger", `#${triggerId}`);
        return;
      }

      drawer.removeAttribute("trigger");
      drawer.hide?.();
    };

    apply();
    if (drawer.__themeDrawerBreakpointInstalled) return;
    drawer.__themeDrawerBreakpointInstalled = true;
    media.addEventListener?.("change", apply);
  }

  function ensureFilterDrawer(block, triggerId, drawerId) {
    const drawerHost = block.querySelector("psykhe-provider") || block;
    let drawer = document.getElementById(drawerId);
    if (!drawer) {
      drawer = document.createElement("psykhe-drawer");
      drawer.id = drawerId;
      drawer.className = "psykhe-noli-filter-drawer";
      drawerHost.append(drawer);
    } else if (drawer.parentElement !== drawerHost) {
      drawerHost.append(drawer);
    }
    drawer.setAttribute("placement", "right");
    drawer.setAttribute("heading", "FILTER");
    syncFilterDrawerTrigger(drawer, triggerId);
    ensureDrawerFilterPanel(drawer);
    return drawer;
  }

  function renderThemeActiveFilterChip(chip, ctx) {
    const valueLabel = String(chip?.label || "");
    const fullLabel = chip.facetLabel ? `${chip.facetLabel}: ${valueLabel}` : valueLabel;
    const showSwatch = isColorFacet(chip);

    return ctx.html`
      <button
        class="active-filters__active-filter fs-body-75 no-transition"
        part="chip"
        type="button"
        aria-label=${`Remove filter: ${fullLabel}`}
        @click=${ctx.actions.remove(chip)}
      >
        <span part="chip-label" aria-hidden="true">
          ${showSwatch
            ? ctx.html`<span
                class="active-filters__swatch"
                part="chip-swatch"
                style=${swatchStyle({ value: swatchValueFromTheme(valueLabel) })}
              ></span>`
            : ctx.nothing}
          <span part="chip-value-label">${valueLabel}</span>
        </span>
        <span part="remove" aria-hidden="true">
          <span class="icon icon-new icon-close-small" part="chip-remove-icon" style="display:block;width:8px;height:8px;">
            <svg viewBox="0 0 24 24" fill="none" focusable="false" xmlns="http://www.w3.org/2000/svg" style="display:block;width:100%;height:100%;">
              <path d="M4.717 1.975 3.347.605.604 3.346l1.37 1.37 2.742-2.74zm14.566 20.05 1.37 1.37 2.742-2.741-1.37-1.37-2.742 2.74zm2.742-17.308 1.37-1.37L20.654.604l-1.37 1.37 2.74 2.742zM1.975 19.283l-1.37 1.37 2.741 2.742 1.37-1.37-2.74-2.742zm0-14.566 8.654 8.654 2.742-2.742-8.654-8.654-2.742 2.742zm11.396 8.654 8.654-8.654-2.742-2.742-8.654 8.654 2.742 2.742zm-2.742-2.742-8.654 8.654 2.742 2.741 8.654-8.653-2.742-2.742zm0 2.742 8.654 8.653 2.742-2.74-8.654-8.655-2.742 2.742z" fill="currentColor"></path>
            </svg>
          </span>
        </span>
      </button>
    `;
  }

  function renderThemeActiveFiltersClearAll(ctx) {
    return ctx.html`
      <button
        class="filter-bar__clear-all"
        part="clear-all"
        type="button"
        @click=${ctx.actions.clearAll()}
      >
        Clear all
      </button>
    `;
  }

  function ensureActiveFiltersRow(block, results, afterElement, drawer) {
    results.querySelectorAll(".psykhe-noli-active-filters").forEach((row) => row.remove());

    const activeRows = Array.from(block.querySelectorAll(".psykhe__active-filters-row"));
    const row = activeRows[0];
    if (!row) return;
    activeRows.slice(1).forEach((staleRow) => staleRow.remove());

    Array.from(block.querySelectorAll("psykhe-active-filters")).forEach((activeFilters) => {
      if (!row.contains(activeFilters)) activeFilters.remove();
    });

    Array.from(block.querySelectorAll("psykhe-clear-filters")).forEach((clearFilters) => {
      if (!drawer?.contains(clearFilters)) clearFilters.remove();
    });

    Array.from(row.querySelectorAll("psykhe-active-filters")).slice(1).forEach((activeFilters) => {
      activeFilters.remove();
    });

    row.querySelectorAll("psykhe-clear-filters").forEach((clearFilters) => clearFilters.remove());

    row.classList.add("filter-bar__active-filters", "psykhe-noli-native-active-filters");
    const drawerFooterActiveFilters = drawer?.querySelector(".psykhe-noli-filter-drawer__footer-active-filters");

    const activeFilters = row.querySelector("psykhe-active-filters");
    if (activeFilters) {
      activeFilters.renderChip = renderThemeActiveFilterChip;
      activeFilters.renderClearAll = renderThemeActiveFiltersClearAll;
      activeFilters.requestUpdate?.();
    }

    const place = () => {
      const useDrawer = isMobileControlsViewport() && drawerFooterActiveFilters;
      row.classList.toggle("psykhe-noli-active-filters-in-drawer", Boolean(useDrawer));

      if (useDrawer) {
        if (row.parentElement !== drawerFooterActiveFilters) drawerFooterActiveFilters.append(row);
        return;
      }

      const reference = afterElement?.nextSibling || null;
      if (row.parentElement !== results || row !== reference) {
        results.insertBefore(row, reference);
      }
    };

    row.__themePlaceActiveFilters = place;
    if (!row.__themeActiveFiltersPlacementInstalled) {
      row.__themeActiveFiltersPlacementInstalled = true;
      window.matchMedia("(max-width: 749px)").addEventListener?.("change", place);
    }

    place();
  }

  function setDesktopFiltersExpanded(block, expanded) {
    block.classList.toggle(FILTERS_EXPANDED_CLASS, expanded);

    const desktopToggle = block.querySelector(".psykhe-noli-filter-toggle");
    desktopToggle?.setAttribute("aria-expanded", String(expanded));

    if (!expanded) {
      block.querySelector(".psykhe-noli-toolbar-filter")?.closeAllFacets?.();
    }
  }

  function installDesktopFilterClickAway(block) {
    if (block.__themeDesktopFilterClickAwayVersion === VERSION) return;

    if (block.__themeDesktopFilterClickAwayHandler) {
      document.removeEventListener("click", block.__themeDesktopFilterClickAwayHandler, true);
    }

    const handler = (event) => {
      if (isMobileControlsViewport()) return;
      if (!block.classList.contains(FILTERS_EXPANDED_CLASS)) return;

      const path = event.composedPath();
      const desktopToggle = block.querySelector(".psykhe-noli-filter-toggle");
      const toolbarFilter = block.querySelector(".psykhe-noli-toolbar-filter");
      const activeFiltersRow = block.querySelector(
        ".psykhe-noli-native-active-filters:not(.psykhe-noli-active-filters-in-drawer)",
      );

      if (desktopToggle && path.includes(desktopToggle)) {
        return;
      }

      if (toolbarFilter?.closeFacetForOutsideClick?.(event)) {
        return;
      }

      if (toolbarFilter && path.includes(toolbarFilter)) {
        return;
      }

      if (activeFiltersRow && path.includes(activeFiltersRow)) {
        toolbarFilter?.closeAllFacets?.();
        return;
      }

      if (toolbarFilter?.closeAllFacets?.()) {
        return;
      }

      setDesktopFiltersExpanded(block, false);
    };

    block.__themeDesktopFilterClickAwayHandler = handler;
    block.__themeDesktopFilterClickAwayVersion = VERSION;
    document.addEventListener("click", handler, true);
  }

  function ensureControlLayout() {
    scopedBlocks().forEach((block, index) => {
      const controls = block.querySelector(".psykhe__controls");
      const results = block.querySelector(".psykhe__results");
      const filters = block.querySelector(".psykhe__filters");
      const count = controls?.querySelector("psykhe-results-count, psykhe-result-count");
      if (!controls || !results || !filters) return;
      if (count?.tagName?.toLowerCase() === "psykhe-results-count") {
        count.setAttribute("singular-label", "Result");
        count.setAttribute("plural-label", "Results");
        count.singularLabel = "Result";
        count.pluralLabel = "Results";
      }

      const blockId = safeId(block.id || `block-${index}`);
      const desktopToggleId = `psykhe-noli-filter-toggle-${blockId}`;
      const mobileTriggerId = `psykhe-noli-filter-drawer-trigger-${blockId}`;
      const drawerId = `psykhe-noli-filter-drawer-${blockId}`;

      const desktopToggles = Array.from(block.querySelectorAll(".psykhe-noli-filter-toggle"));
      let desktopToggle = document.getElementById(desktopToggleId) || desktopToggles[0];
      desktopToggles.forEach((toggle) => {
        if (toggle !== desktopToggle) toggle.remove();
      });
      if (!desktopToggle) {
        desktopToggle = createControlButton(
          "filter-bar__button filter-bar__button--filters psykhe-noli-filter-toggle",
          "Filter",
          desktopToggleId,
        );
        controls.insertBefore(desktopToggle, count || controls.firstChild);
      }
      desktopToggle.className = "filter-bar__button filter-bar__button--filters psykhe-noli-filter-toggle";
      desktopToggle.querySelector("span:not(.icon)")?.classList.add("filter-bar__button-text");
      desktopToggle.id = desktopToggleId;
      desktopToggle.setAttribute("aria-expanded", String(block.classList.contains(FILTERS_EXPANDED_CLASS)));
      if (!desktopToggle.__themeFilterToggleInstalled) {
        desktopToggle.__themeFilterToggleInstalled = true;
        desktopToggle.addEventListener("click", () => {
          const expanded = !block.classList.contains(FILTERS_EXPANDED_CLASS);
          setDesktopFiltersExpanded(block, expanded);
        });
      }
      if (desktopToggle.parentElement !== controls) {
        controls.insertBefore(desktopToggle, count || controls.firstChild);
      }

      const mobileTriggers = Array.from(block.querySelectorAll(".psykhe-noli-mobile-filter-trigger"));
      let mobileTrigger = document.getElementById(mobileTriggerId) || mobileTriggers[0];
      mobileTriggers.forEach((trigger) => {
        if (trigger !== mobileTrigger) trigger.remove();
      });
      if (!mobileTrigger) {
        mobileTrigger = createControlButton("psykhe-noli-mobile-filter-trigger", "Filter", mobileTriggerId);
        controls.insertBefore(mobileTrigger, count || controls.firstChild);
      }
      mobileTrigger.id = mobileTriggerId;
      mobileTrigger.setAttribute("aria-haspopup", "dialog");
      if (mobileTrigger.parentElement !== controls) {
        controls.insertBefore(mobileTrigger, count || controls.firstChild);
      }
      if (!mobileTrigger.__themeMobileTriggerGuardInstalled) {
        mobileTrigger.__themeMobileTriggerGuardInstalled = true;
        mobileTrigger.addEventListener(
          "click",
          (event) => {
            if (isMobileControlsViewport()) return;
            event.preventDefault();
            event.stopImmediatePropagation();
          },
          true,
        );
      }

      const toolbarFilter = filters.querySelector("psykhe-filter-panel") || block.querySelector(".psykhe-noli-toolbar-filter");
      if (toolbarFilter) {
        toolbarFilter.classList.add("psykhe-noli-toolbar-filter");
        configureFilterPanel(toolbarFilter, "bar");
        if (toolbarFilter.parentElement !== results || toolbarFilter.previousElementSibling !== controls) {
          results.insertBefore(toolbarFilter, controls.nextSibling);
        }
        desktopToggle.setAttribute("aria-controls", toolbarFilter.id || `${desktopToggleId}-panel`);
        if (!toolbarFilter.id) toolbarFilter.id = `${desktopToggleId}-panel`;
      }

      const drawer = ensureFilterDrawer(block, mobileTriggerId, drawerId);
      ensureActiveFiltersRow(block, results, toolbarFilter || controls, drawer);
      installDesktopFilterClickAway(block);
    });
  }

  function productPath(product) {
    return product?.handle ? `/products/${product.handle}` : "#";
  }

  function withShopifyWidth(url, width) {
    if (!url || !width || !url.includes("cdn.shopify.com")) return url;
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}width=${width}`;
  }

  function rawImageUrl(image) {
    const url = typeof image === "string" ? image : image?.url;
    if (!url) return "";
    if (typeof url !== "string") return "";
    if (url.startsWith("//")) return `${window.location.protocol}${url}`;
    return url;
  }

  function imageUrl(image, width) {
    return withShopifyWidth(rawImageUrl(image), width);
  }

  function activeShopifyCurrencyCode() {
    return window.Shopify?.currency?.active || shopCurrencyCode() || null;
  }

  function activeShopifyCurrencyRate() {
    const parsed = Number(window.Shopify?.currency?.rate);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  function shopifyPresentmentPriceResolver() {
    const factory = window.PsykheSDK?.shopify?.createPresentmentPriceResolver;
    if (typeof factory !== "function") return null;
    if (!presentmentPriceResolver || presentmentPriceResolverFactory !== factory) {
      presentmentPriceResolverFactory = factory;
      presentmentPriceResolver = factory({
        lookupStorageKey: SHOPIFY_PRICE_LOOKUP_STORAGE_KEY,
        currency: activeShopifyCurrencyCode(),
        rate: activeShopifyCurrencyRate() ?? 0,
        locale: storefrontLocale(),
      });
    }
    return presentmentPriceResolver;
  }

  // Price display uses the public Shopify helper installed on window.PsykheSDK
  // by the PSYKHE app-extension widget bundle. That bundle is loaded by the
  // collection app block; it is not a separate file in this theme assets folder.
  // The helper checks the Shopify presentment lookup first, then Shopify's active
  // storefront currency rate, then preserves the original product price/currency
  // from the collection API. This asset only schedules a re-render when the theme
  // writes the lookup map after the grid has already rendered.
  function productForCardPriceDisplay(product) {
    const resolver = shopifyPresentmentPriceResolver();
    if (!resolver) return product;
    const resolution = resolver.resolve(product);
    if (!resolution.lookupAvailable) schedulePriceLookupRefresh();
    return resolution.product;
  }

  function schedulePriceLookupRefresh() {
    const resolver = shopifyPresentmentPriceResolver();
    if (!resolver) return;
    if (priceLookupRefreshTimer || priceLookupRefreshAttempts >= PRICE_LOOKUP_RETRY_LIMIT) return;
    priceLookupRefreshAttempts += 1;
    priceLookupRefreshTimer = window.setTimeout(() => {
      priceLookupRefreshTimer = null;
      resolver.invalidate();
      if (resolver.hasCurrentCountryLookup()) {
        priceLookupRefreshAttempts = 0;
        scopedElements("psykhe-product-grid").forEach((grid) => grid.requestUpdate?.());
        return;
      }
      schedulePriceLookupRefresh();
    }, PRICE_LOOKUP_RETRY_DELAY_MS);
  }

  // product.price is the original Shopify catalog price from the product feed in
  // the base catalog currency.
  // Prefer a Shopify presentment-price lookup when available; fall back to this
  // catalog price path when the lookup is missing or still loading.
  function currentMoney(product) {
    return product?.price?.current || null;
  }

  function compareAtMoney(product) {
    return product?.price?.compareAt || null;
  }

  function moneyAmount(value) {
    return typeof value?.amount === "number" && Number.isFinite(value.amount) ? value.amount : null;
  }

  function discountPercent(product) {
    const current = moneyAmount(currentMoney(product));
    const compare = moneyAmount(compareAtMoney(product));
    if (current == null || compare == null || compare <= current) return null;
    return Math.round(((compare - current) / compare) * 100);
  }

  function moneyFormatTemplate() {
    return providerAttribute("money-format") || "${{amount}}";
  }

  function moneyFormatForProduct(product) {
    const currency = currentMoney(product)?.currency || null;
    const activeCurrency = activeShopifyCurrencyCode();
    return !currency || !activeCurrency || currency === activeCurrency ? moneyFormatTemplate() : "";
  }

  // Mirrors the storefront swatch priority: product image, color-named image
  // asset, custom swatch color, then browser CSS color name. These aliases cover
  // labels that browsers cannot resolve as CSS colors.
  function colorFromName(name) {
    const normalized = String(name || "").toLowerCase();
    if (normalized.includes("black")) return "#111111";
    if (normalized.includes("white") || normalized.includes("blanc")) return "#f7f5ef";
    if (normalized.includes("navy")) return "#1d2a44";
    if (normalized.includes("blue")) return "#8fb3d9";
    if (normalized.includes("blush")) return "#f7c9d1";
    if (normalized.includes("champagne")) return "#d8c4a1";
    if (normalized.includes("cloud")) return "#d9eee9";
    if (normalized.includes("vanilla")) return "#f3ecd7";
    if (normalized.includes("orchid")) return "#c54fb0";
    return "#d8d8d8";
  }

  function readSiblingSwatchPayload() {
    const node = document.getElementById(SIBLING_SWATCH_DATA_ID);
    if (!node?.textContent) return null;
    try {
      return JSON.parse(node.textContent);
    } catch {
      return null;
    }
  }

  function siblingSwatchData() {
    if (siblingSwatchDataCache) return siblingSwatchDataCache;

    const raw = readSiblingSwatchPayload();
    if (!raw || typeof raw !== "object") {
      return { products: {}, swatchColors: {}, filesUrl: "" };
    }

    siblingSwatchDataCache = {
      products: raw.products && typeof raw.products === "object" ? raw.products : {},
      swatchColors: raw.swatchColors && typeof raw.swatchColors === "object" ? raw.swatchColors : {},
      filesUrl: typeof raw.filesUrl === "string" ? raw.filesUrl : "",
    };

    return siblingSwatchDataCache;
  }

  function swatchValueFromTheme(name) {
    const value = String(siblingSwatchData().swatchColors?.[name] || "").trim();
    if (!value) return colorFromName(name);
    if (/^https?:\/\//.test(value) || value.startsWith("//")) return value;
    if (/\.(png|jpe?g|webp|gif|svg)$/i.test(value)) {
      return `${siblingSwatchData().filesUrl}${value}`;
    }
    return value;
  }

  function productLookupKeys(product) {
    const rawKeys = [
      product.product_identifier,
      product.productIdentifier,
      product.product_id,
      product.productId,
      product.id,
      product.handle,
    ]
      .filter((key) => key !== undefined && key !== null && key !== "")
      .map((key) => String(key));

    const keys = new Set();
    rawKeys.forEach((key) => {
      keys.add(key);
      keys.add(key.replace(/^gid:\/\/shopify\/Product\//, ""));
    });
    return Array.from(keys).filter(Boolean);
  }

  function mappedSiblingSwatches(product) {
    const data = siblingSwatchData();
    const siblings = productLookupKeys(product)
      .map((key) => data.products[key])
      .find((value) => Array.isArray(value));
    if (!siblings?.length) return [];

    const currentKeys = new Set(productLookupKeys(product));
    return siblings
      .map((sibling) => {
        if (!sibling?.handle) return null;
        const name = sibling.color || sibling.handle;
        const productId = sibling.productId != null ? String(sibling.productId) : "";
        return {
          name,
          value: sibling.swatch || swatchValueFromTheme(name),
          href: productPath(sibling),
          handle: sibling.handle,
          productId,
          selected: product.handle === sibling.handle || (productId && currentKeys.has(productId)),
          source: "liquid-sibling-map",
        };
      })
      .filter(Boolean);
  }

  function swatchItems(product) {
    const mappedSwatches = mappedSiblingSwatches(product);
    if (mappedSwatches.length) return mappedSwatches;

    if (product.swatches?.length) {
      return product.swatches.map((swatch) => ({
        name: swatch.name,
        value: swatch.value,
        variantId: swatch.variantId,
      }));
    }

    return [];
  }

  function swatchStyle(swatch) {
    const value = String(swatch.value || "").trim();
    if (/^https?:\/\//.test(value) || value.startsWith("//")) {
      return `background-image:url("${imageUrl({ url: value }, 80)}");`;
    }
    return `background:${value || "#d8d8d8"};`;
  }

  function productForSourceCardNavigation(product, href) {
    // Sibling swatches navigate to another PDP, but browser back should restore
    // the source collection card that the shopper clicked from.
    if (!href || href === product.url) return product;
    return { ...product, url: href };
  }

  function sourceCardNavigationClickHandler(product, href, index) {
    return (event) => {
      const mouse = event instanceof MouseEvent ? event : null;
      const target = event.currentTarget?.getAttribute?.("target") || null;
      const normalizedTarget = String(target || "_self").toLowerCase();
      const modified = Boolean(mouse && (mouse.metaKey || mouse.ctrlKey || mouse.shiftKey || mouse.altKey));
      const navigationHref = href || event.currentTarget?.getAttribute?.("href") || product.url || null;
      event.currentTarget?.dispatchEvent?.(
        new CustomEvent("psykhe-product-click", {
          bubbles: true,
          composed: true,
          detail: {
            card: productForSourceCardNavigation(product, navigationHref),
            index,
            navigationIntent: {
              href: navigationHref,
              target,
              sameTab: navigationHref !== null && (mouse?.button ?? 0) === 0 && !modified && normalizedTarget === "_self",
              modified,
              button: mouse?.button ?? 0,
            },
          },
        }),
      );
    };
  }

  function renderCardSwatch(ctx, product, swatch, index) {
    const label = swatch.name || "Color";
    const className = `noli-card__swatch${swatch.selected ? " noli-card__swatch--selected" : ""}`;
    const partName = `noli-card-swatch${swatch.selected ? " noli-card-swatch-selected" : ""}`;
    const style = swatchStyle(swatch);

    if (swatch.href) {
      return ctx.html`<a
        class=${className}
        part=${partName}
        href=${swatch.href}
        style=${style}
        title=${label}
        aria-label=${label}
        data-sibling-swatch
        data-sibling-cutline=${label}
        @click=${sourceCardNavigationClickHandler(product, swatch.href, index)}
      ></a>`;
    }

    return ctx.html`<span class=${className} part=${partName} style=${style} title=${label} aria-label=${label}></span>`;
  }

  function renderCard(product, ctx, meta) {
    const displayProduct = productForCardPriceDisplay(product);
    const price = currentMoney(displayProduct);
    const compare = compareAtMoney(displayProduct);
    const discount = discountPercent(displayProduct);
    const swatches = swatchItems(product);
    const productHref = productPath(product);
    const visibleIndex = meta?.index ?? 0;
    const extraClass = discount ? "noli-card__extra noli-card__extra--with-discount" : "noli-card__extra";
    const extraPart = discount ? "noli-card-extra noli-card-extra-with-discount" : "noli-card-extra";

    return ctx.html`
      <psykhe-product-card
        class="noli-card product-item product-item--with-hover-swatches"
        part="noli-card"
        exportparts=${CARD_EXPORTPARTS}
        data-product-id=${product.id}
        .product=${displayProduct}
        .index=${visibleIndex}
        .currency=${price?.currency || compare?.currency || "USD"}
        .showVendor=${false}
        .showCompareAtPrice=${true}
        .showSaleBadge=${false}
        .showHoverAction=${false}
        .showGallery=${false}
        .showSalePriceFirst=${true}
        .moneyFormat=${moneyFormatForProduct(displayProduct)}
      >
        ${(discount || swatches.length)
          ? ctx.html`<div slot="actions" class=${extraClass} part=${extraPart}>
          ${discount
            ? ctx.html`<div class="noli-card__discount" part="noli-card-discount">${discount}% Off. Price as Marked.</div>`
            : ctx.nothing}
          ${swatches.length
            ? ctx.html`<div class="noli-card__swatches" part="noli-card-swatches" aria-label=${`${swatches.length} colors`}>
                ${swatches.slice(0, 5).map((swatch) => renderCardSwatch(ctx, product, swatch, visibleIndex))}
                ${swatches.length > 5
                  ? ctx.html`<a
                      class="noli-card__more"
                      part="noli-card-more"
                      href=${productHref}
                      aria-label=${`View all ${swatches.length} colors for ${product.title}`}
                      @click=${sourceCardNavigationClickHandler(product, productHref, visibleIndex)}
                    >+${swatches.length - 5}</a>`
                  : ctx.nothing}
              </div>`
            : ctx.nothing}
        </div>`
          : ctx.nothing}
      </psykhe-product-card>
    `;
  }

  function installGridRenderer() {
    const grids = scopedElements("psykhe-product-grid");
    if (!grids.length || !customElements.get("psykhe-product-grid")) return false;
    grids.forEach((grid) => {
      if (grid.__themePsykheInstalled === VERSION) return;
      grid.__themePsykheInstalled = VERSION;
      grid.renderCard = renderCard;
      grid.requestUpdate?.();
    });
    return true;
  }

  function installControlTweaks() {
    ensureControlLayout();

    if (customElements.get("psykhe-sort-select")) {
      scopedElements("psykhe-sort-select").forEach((sort) => {
        sort.prefix = "Sort: ";
        sort.icon = CONTROL_CHEVRON;
        sort.indicator = "dot";
        sort.renderOption = renderThemeSortOption;
        sort.setAttribute("prefix", "Sort: ");
        sort.setAttribute("icon", CONTROL_CHEVRON);
        sort.setAttribute("indicator", "dot");
      });
    }

    scopedElements("psykhe-filter-panel").forEach((panel) => {
      configureFilterPanel(panel, panel.classList.contains("psykhe-noli-drawer-filter-panel") ? "accordion" : "bar");
    });
  }

  function install() {
    installControlTweaks();
    const installed = installGridRenderer();
    if (!installed) {
      installAttempts += 1;
      if (installAttempts >= MAX_INSTALL_ATTEMPTS) {
        return;
      }
      window.setTimeout(install, 120);
      return;
    }
    installAttempts = 0;
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", install, { once: true });
  } else {
    install();
  }

  Promise.allSettled([
    customElements.whenDefined("psykhe-product-grid"),
    customElements.whenDefined("psykhe-sort-select"),
  ]).then(install);
})();
